/**
 * #1023 — shared by the manager US sections of Trader Detail and Org Detail:
 * the role-based, org-scoped `/api/manager/polymarket-us` reads and their
 * presentation helpers.
 */
import { Loader2 } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import type { UsFill } from '@/lib/polymarketUs/fillsClient';
import { US_CONFLICT_TITLE, US_PROVISIONAL_TITLE, usPriceDashTitle } from '@/lib/fills/usPrice';
import { mapUsInventory } from '@/lib/polymarketUs/portfolioClient';
import type { UsInventoryFill } from '@/lib/polymarketUs/fillProgress';
import type { UsPositionRow } from '@/lib/polymarketUs/types';

export const US_MANAGER_API = '/api/manager/polymarket-us';
export const dash = (title: string) => <span title={title}>—</span>;

/** #1023: the trader's holdings across the org's US accounts, through the
 *  tracker's own inventory mapping. `complete` only when every account read is. */
export type TraderPositions = { knownHoldings: ReadonlySet<string>; orderFills: ReadonlyMap<string, readonly UsInventoryFill[]>; rows: UsPositionRow[]; complete: boolean;
  /** Market slug → title, as US orders are named (#1026); absent when neither source knows it. */
  titles: ReadonlyMap<string, string> };
/** Exact USD decimal text → cents text, without floating point ("0.575" → "57.5"). */
export function usdTextToCents(usd: string): string {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(usd);
  if (!m) return usd;
  const frac = (m[2] ?? '').padEnd(2, '0');
  const whole = (BigInt(m[1]) * 100n + BigInt(frac.slice(0, 2))).toString();
  const rest = frac.slice(2).replace(/0+$/, '');
  return rest ? `${whole}.${rest}` : whole;
}
/** The positions read, or null when it failed or any account's read is malformed
 *  (one bad account refuses the whole read, as the tracker's mapping does). */
export function traderPositions(body: unknown, traderId: string): TraderPositions | null {
  const b = body as { accounts?: unknown; complete?: unknown } | null;
  if (!Array.isArray(b?.accounts)) return null;
  try {
    const rows: UsPositionRow[] = [];
    const knownHoldings = new Set<string>();
    const orderFills = new Map<string, readonly UsInventoryFill[]>();
    const titles = new Map<string, string>();
    let complete = b.complete === true;
    for (const entry of b.accounts as { accountId?: unknown; inventory?: unknown }[]) {
      if (typeof entry?.accountId !== 'string') return null;
      const read = mapUsInventory(entry.inventory, { accountId: entry.accountId, userId: traderId },
        { venue: 'polymarket_us', scopeKey: `manager:${traderId}:${entry.accountId}`, revision: 'trader-detail' });
      rows.push(...read.positions);
      for (const p of read.positions) if (p.heldQuantity !== null && !p.reasons.length) {
        knownHoldings.add(JSON.stringify([entry.accountId, p.target.marketSlug, p.target.outcome]));
      }
      orderFills.set(entry.accountId, read.orderFills);
      for (const r of (entry.inventory as { rows?: { marketSlug?: unknown; marketTitle?: unknown }[] }).rows ?? []) {
        if (typeof r.marketSlug === 'string' && typeof r.marketTitle === 'string' && r.marketTitle) titles.set(r.marketSlug, r.marketTitle);
      }
      if (read.coverage.state !== 'complete') complete = false;
    }
    // A holding known to be exactly zero is closed, like K/P open positions.
    return { knownHoldings, orderFills, rows: rows.filter((r) => r.heldQuantity === null || !/^-?0(?:\.0+)?$/.test(r.heldQuantity)), complete, titles };
  } catch {
    return null;
  }
}
export async function readJson(url: string): Promise<unknown | null> {
  try {
    const res = await apiFetch(url);
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}


/** The relay's fill row (same read as the Recent Fills rail). #1015: a NO price is provisional, and a
 *  conflicting row is shown with its disputed fields null (market, side and action together). */
export type ManagerUsFill = Pick<UsFill, 'executionId' | 'marketSlug' | 'marketTitle' | 'outcome' | 'action' | 'quantity' | 'price'
  | 'priceBasis' | 'executedAt' | 'evidenceState' | 'disputed'>;

export function UsFillMarket({ fill: f }: { fill: ManagerUsFill }) {
  return (
    <>
      {f.evidenceState === 'conflicting' && <span className="mr-1 text-amber-400 light:text-amber-700" title={US_CONFLICT_TITLE}>⚠</span>}
      {f.marketTitle ?? f.marketSlug ?? 'market disputed'}
    </>
  );
}
export function UsFillPrice({ fill: f }: { fill: ManagerUsFill }) {
  return (
    <>
      {f.price === null ? dash(usPriceDashTitle(f.priceBasis)) : `${usdTextToCents(f.price)}¢`}
      {f.priceBasis === 'provisional' && <span className="text-muted-foreground/60" title={US_PROVISIONAL_TITLE}>*</span>}
    </>
  );
}
export function UsFillTime({ fill: f }: { fill: ManagerUsFill }) {
  return f.disputed.includes('executedAt') ? dash('time disputed')
    : <>{f.executedAt ? new Date(f.executedAt).toLocaleString() : '—'}</>;
}

/** Whether the org has a US account. A failed, refused or malformed read is
 *  `failed`, never `absent`: only a successful answer listing no accounts
 *  means the org has no US (#1023 review). */
export type UsAccountCheck = 'present' | 'absent' | 'failed';
export async function checkUsAccounts(orgQueryParam: string): Promise<UsAccountCheck> {
  const body = await readJson(`${US_MANAGER_API}/accounts${orgQueryParam}`) as { accounts?: unknown } | null;
  if (body === null || !Array.isArray(body.accounts)) return 'failed';
  return body.accounts.length > 0 ? 'present' : 'absent';
}

/** Retry through the page's own Refresh (the same handler as its Refresh button).
 *  `busy` while that re-read is in flight: the button stays in place, disabled,
 *  so a second press cannot stack another read behind the first. */
export function UsRetry({ onRetry, busy = false }: { onRetry?: () => void; busy?: boolean }) {
  if (!onRetry) return null;
  return (
    <button type="button" onClick={onRetry} disabled={busy} aria-busy={busy || undefined}
      className="text-foreground hover:bg-muted border-border ml-2 rounded border px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent">
      Retry
    </button>
  );
}

/** The section's place on the page while the account check is pending, or after
 *  it failed: the section never just disappears on an unknown answer. */
export function UsStatusRegion({ state, testId, onRetry, busy = false }: {
  state: 'loading' | 'failed'; testId: string; onRetry?: () => void; busy?: boolean;
}) {
  return (
    <div className="bg-card border-border flex items-center gap-2 rounded-lg border px-4 py-3 text-sm" data-testid={testId}
      role="status" aria-busy={state === 'loading'}>
      {state === 'loading' ? (
        <>
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" aria-hidden />
          <span className="text-muted-foreground">Checking Polymarket US…</span>
        </>
      ) : (
        <>
          <span className="text-amber-500 light:text-amber-700">Polymarket US could not be read — it may or may not have accounts here.</span>
          <UsRetry onRetry={onRetry} busy={busy} />
        </>
      )}
    </div>
  );
}
